import { formatToDecimals } from '~/utils/number-helper';
import { zeroIfNaN } from '~/utils/math-helper';
import { AddBudgetCompensationEntry } from '~/models/services/minifd';
import { LatestBudgetRequestDataResponse } from '~/models/services/budget-request';
import { CompensationRequiredTotals } from '~/components/in-year-budget-requests/create-in-year-budget-request/create-iybr-form/create-iybr-form-model';

type CompensationAvailableTotals = {
    mtpYearPlus1Total: number;
    mtpYearPlus2Total: number;
    mtpYearPlus3Total: number;
};

type CompensationEntryBounds = {
    yearPlus1: { value: string };
    yearPlus2: { value: string };
    yearPlus3: { value: string };
    originalYearPlus1: { value: number | string };
    originalYearPlus2: { value: number | string };
    originalYearPlus3: { value: number | string };
};

function isUnusedCompensationYearValue(value: string | undefined | null): boolean {
    return (value ?? '').toString().trim() === '';
}

function isCompensationYearValueWithinOriginal(value: string, original: number | string): boolean {
    if (isUnusedCompensationYearValue(value)) {
        return true;
    }

    const parsed = zeroIfNaN(parseFloat(value.toString().trim()));
    if (parsed === 0) {
        return true;
    }

    const parsedRounded = formatToDecimals(parsed, 3);
    const originalRounded = formatToDecimals(Number(original), 3);
    return parsedRounded >= 0 && parsedRounded <= originalRounded;
}

export function isCompensationEntryWithinOriginal(entry: CompensationEntryBounds): boolean {
    return (
        isCompensationYearValueWithinOriginal(entry.yearPlus1.value, entry.originalYearPlus1.value) &&
        isCompensationYearValueWithinOriginal(entry.yearPlus2.value, entry.originalYearPlus2.value) &&
        isCompensationYearValueWithinOriginal(entry.yearPlus3.value, entry.originalYearPlus3.value)
    );
}

export function doesCompensationAvailableExceedRequired(available: number, required: number): boolean {
    const availableRounded = formatToDecimals(available, 3);
    const requiredRounded = formatToDecimals(required, 3);
    return availableRounded !== 0 && availableRounded > requiredRounded;
}

export function doesCompensationTotalsExceedRequired(
    available: CompensationAvailableTotals,
    required: CompensationRequiredTotals
): boolean {
    return (
        doesCompensationAvailableExceedRequired(available.mtpYearPlus1Total, required.mtpPlus1Required) ||
        doesCompensationAvailableExceedRequired(available.mtpYearPlus2Total, required.mtpPlus2Required) ||
        doesCompensationAvailableExceedRequired(available.mtpYearPlus3Total, required.mtpPlus3Required)
    );
}

/**
 * Prefer explicit requested* fields; fall back to legacy stored residual (original - requested).
 */
function resolveRequestedCompensationAmount(
    requested: number | undefined,
    storedYearPlus: number | undefined,
    originalNew: number
): number {
    if (requested != null && !Number.isNaN(requested)) {
        return formatToDecimals(requested, 3);
    }

    if (storedYearPlus != null && !Number.isNaN(storedYearPlus) && storedYearPlus !== 0) {
        return formatToDecimals(Math.max(0, originalNew - storedYearPlus), 3);
    }

    return 0;
}

export function getCompensationYearValues(
    entry: Pick<
        AddBudgetCompensationEntry,
        'requestedYearPlus1' | 'requestedYearPlus2' | 'requestedYearPlus3' | 'yearPlus1' | 'yearPlus2' | 'yearPlus3'
    >,
    budgetRequest: Pick<LatestBudgetRequestDataResponse, 'new1' | 'new2' | 'new3'>
): { yearPlus1: string; yearPlus2: string; yearPlus3: string } {
    return {
        yearPlus1: resolveRequestedCompensationAmount(entry.requestedYearPlus1, entry.yearPlus1, budgetRequest.new1).toString(),
        yearPlus2: resolveRequestedCompensationAmount(entry.requestedYearPlus2, entry.yearPlus2, budgetRequest.new2).toString(),
        yearPlus3: resolveRequestedCompensationAmount(entry.requestedYearPlus3, entry.yearPlus3, budgetRequest.new3).toString(),
    };
}
